import { useLocaliserVitrine } from './localiser'
/**
 * Le formulaire de contact de la vitrine — la dernière marche du récit.
 *
 * Il vient **après le tarif et l’éditeur** : à cet endroit de la page, le
 * lecteur sait ce que fait le logiciel, ce qu’il coûte et qui l’écrit. Poser la
 * question plus tôt, c’est demander un courriel à quelqu’un qui ne sait pas
 * encore s’il est intéressé.
 *
 * ★ **Pourquoi un vrai formulaire, et pas un lien `mailto:`.** Un `mailto:`
 *   suppose un client de messagerie configuré sur le poste. Au bureau d’un
 *   théâtre, sur un téléphone dont le compte professionnel n’est pas branché,
 *   dans un navigateur où le webmail n’est pas déclaré, le clic ne fait
 *   littéralement rien : la personne croit avoir écrit, personne ne reçoit
 *   rien, et il n’en reste aucune trace côté éditeur. Une demande commerciale
 *   perdue ne se rattrape pas — on ne sait même pas qu’elle a existé.
 *
 * ★ **Le lien vers ilygo.ch et l’IDE ne sont pas répétés ici** : ils vivent
 *   dans `SectionEditeur`, juste au-dessus. L’adresse, elle, réapparaît à un
 *   seul endroit — dans l’état d’échec, où elle est la sortie de secours.
 *
 * ★ **Trois gardes anti-robots**, dont deux sont visibles dans ce fichier : le
 *   champ piège (invisible à l’écran, toujours vide chez un humain) et la durée
 *   de saisie, mesurée en horloge monotone. La troisième — le plafonnement par
 *   IP — est tenue par le serveur, seul endroit où elle a une autorité.
 *
 * Ce que la page promet, elle le tient : « nous répondons sous 48 heures
 * ouvrables », et rien d’autre. La phrase vient du serveur, qui l’écrit une
 * fois pour toutes.
 */

import { useEffect, useRef, useState } from 'react'

import { messageErreur } from '../../lib/api'
import { BandeauErreur, Button, FormField, Input, Textarea } from '../../ui'
import { envoyerDemandeContact } from './contact'
import { EMAIL_ILYGO, SUJET_CONTACT } from './editeur'
import { EnteteSection, MESURE, Section } from './elements'

/* -------------------------------------------------------------------------- */
/* Saisie                                                                     */
/* -------------------------------------------------------------------------- */

interface Saisie {
  nom: string
  organisation: string
  email: string
  telephone: string
  message: string
}

const VIDE: Saisie = { nom: '', organisation: '', email: '', telephone: '', message: '' }

type Erreurs = Partial<Record<keyof Saisie, string>>

/**
 * La sortie de secours : un message pré-rempli vers l’adresse unique de
 * l’entreprise, dont le sujet nomme déjà le produit (`SUJET_CONTACT`).
 *
 * Elle n’apparaît QUE si le formulaire a refusé de partir. En faire l’offre
 * principale reviendrait à reproduire le défaut du `mailto:` — un clic qui ne
 * fait rien chez qui n’a pas de client de messagerie configuré.
 */
const lienDirect = `mailto:${EMAIL_ILYGO}?subject=${encodeURIComponent(SUJET_CONTACT)}`

/**
 * Contrôles de saisie, volontairement **minimaux et identiques au serveur**.
 *
 * Ils n’existent que pour éviter un aller-retour évitable ; c’est le serveur
 * qui décide. Rejouer ici une validation plus fine créerait une seconde vérité,
 * et c’est toujours la nôtre qui finirait par être fausse.
 */
function verifier(saisie: Saisie): Erreurs {
  const erreurs: Erreurs = {}
  if (saisie.nom.trim().length < 2) erreurs.nom = 'Indiquez votre nom.'
  if (saisie.organisation.trim().length < 2) {
    erreurs.organisation = 'Indiquez le nom de votre salle ou de votre compagnie.'
  }
  const email = saisie.email.trim()
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email)) {
    erreurs.email = 'Indiquez une adresse électronique valable, pour que nous puissions répondre.'
  }
  if (saisie.message.trim().length < 10) {
    erreurs.message = 'Dites-nous en quelques mots ce que vous cherchez.'
  }
  return erreurs
}

/* -------------------------------------------------------------------------- */
/* Section                                                                    */
/* -------------------------------------------------------------------------- */

export function SectionContact() {
  const localiser = useLocaliserVitrine()
  const [saisie, setSaisie] = useState<Saisie>(VIDE)
  /** Champ piège : jamais affiché, jamais rempli par un être humain. */
  const [piege, setPiege] = useState('')
  const [erreurs, setErreurs] = useState<Erreurs>({})
  const [envoi, setEnvoi] = useState(false)
  const [refus, setRefus] = useState<string | null>(null)
  const [succes, setSucces] = useState<string | null>(null)

  /* Horloge MONOTONE : `performance.now()` ne dépend pas de l’heure du poste,
     donc un visiteur dont l’horloge est fausse n’est jamais écarté. Le relevé
     se fait dans un effet, jamais pendant le rendu : lire une horloge est un
     effet de bord, et un rendu doit pouvoir être rejoué à l’identique. */
  const ouvertureMs = useRef<number | null>(null)
  const confirmation = useRef<HTMLParagraphElement | null>(null)

  useEffect(() => {
    ouvertureMs.current = performance.now()
  }, [])

  /** Temps passé sur le formulaire, en millisecondes. */
  function dureeSaisieMs(): number {
    return ouvertureMs.current === null ? 0 : Math.round(performance.now() - ouvertureMs.current)
  }

  function modifier(champ: keyof Saisie, valeur: string): void {
    setSaisie((precedente) => ({ ...precedente, [champ]: valeur }))
    /* L’erreur d’un champ disparaît dès qu’on le corrige : la laisser sous les
       doigts pendant la frappe est une réprimande permanente. */
    setErreurs((precedentes) =>
      precedentes[champ] === undefined ? precedentes : { ...precedentes, [champ]: undefined },
    )
  }

  async function envoyer(evenement: React.FormEvent): Promise<void> {
    evenement.preventDefault()
    if (envoi) return

    const trouvees = verifier(saisie)
    setErreurs(trouvees)
    if (Object.values(trouvees).some((message) => message !== undefined)) return

    setRefus(null)
    setEnvoi(true)
    try {
      const telephone = saisie.telephone.trim()
      const accuse = await envoyerDemandeContact({
        nom: saisie.nom.trim(),
        organisation: saisie.organisation.trim(),
        email: saisie.email.trim(),
        ...(telephone === '' ? {} : { telephone }),
        message: saisie.message.trim(),
        siteWeb: piege,
        dureeSaisieMs: dureeSaisieMs(),
      })
      if (!accuse.envoye) {
        setRefus(accuse.message)
        return
      }
      setSucces(accuse.message)
      setSaisie(VIDE)
      /* Le message de confirmation remplace le formulaire : on y porte le
         focus, sinon le lecteur d’écran reste sur un bouton qui n’existe
         plus et rien n’annonce que la demande est partie. */
      requestAnimationFrame(() => confirmation.current?.focus())
    } catch (cause: unknown) {
      setRefus(messageErreur(cause, 'Votre message n’a pas pu être envoyé.'))
    } finally {
      setEnvoi(false)
    }
  }

  return localiser(
    <Section fond="blanc" libelleTitre="titre-contact">
      <EnteteSection
        acte="15"
        id="titre-contact"
        surtitre="Parlons de votre salle"
        titre={<>Écrivez-nous&nbsp;: nous répondons en deux jours ouvrables</>}
        chapeau="Dites-nous combien vous avez de places, quelles formules vous vendez et ce qui vous coûte le plus de temps aujourd’hui. Nous vous répondons avec un prix et une réponse franche, y compris quand ce logiciel n’est pas ce qu’il vous faut."
      />

      <div className={`mt-8 ${MESURE} sm:mt-10`}>
        {succes === null ? (
          <form className="flex flex-col gap-5" onSubmit={(e) => void envoyer(e)} noValidate>
            {refus !== null && (
              <BandeauErreur
                titre="Message non envoyé"
                message={
                  <>
                    {/* Certains refus nomment déjà l’adresse — le plafond de
                        débit le fait. On ne la répète alors pas : deux fois la
                        même adresse en trois lignes se lit comme un défaut
                        d’affichage. Le lien, lui, est toujours offert : c’est la
                        sortie de secours de ce formulaire, et elle ne doit
                        jamais dépendre du message reçu. */}
                    {refus.includes(EMAIL_ILYGO) ? (
                      <>
                        {refus}{' '}
                        <a href={lienDirect} className="font-medium underline underline-offset-2">
                          Ouvrir un message pré-rempli
                        </a>
                      </>
                    ) : (
                      <>
                        {refus} Écrivez-nous directement à{' '}
                        <a href={lienDirect} className="font-medium underline underline-offset-2">
                          {EMAIL_ILYGO}
                        </a>
                        .
                      </>
                    )}
                  </>
                }
              />
            )}

            <div className="grid gap-5 sm:grid-cols-2">
              <FormField label="Votre nom" requis erreur={erreurs.nom}>
                {(champ) => (
                  <Input
                    {...champ}
                    type="text"
                    autoComplete="name"
                    maxLength={80}
                    invalide={erreurs.nom !== undefined}
                    value={saisie.nom}
                    onChange={(e) => {
                      modifier('nom', e.target.value)
                    }}
                  />
                )}
              </FormField>

              <FormField label="Votre salle ou compagnie" requis erreur={erreurs.organisation}>
                {(champ) => (
                  <Input
                    {...champ}
                    type="text"
                    autoComplete="organization"
                    maxLength={120}
                    invalide={erreurs.organisation !== undefined}
                    value={saisie.organisation}
                    onChange={(e) => {
                      modifier('organisation', e.target.value)
                    }}
                  />
                )}
              </FormField>

              <FormField label="Adresse électronique" requis erreur={erreurs.email}>
                {(champ) => (
                  <Input
                    {...champ}
                    type="email"
                    autoComplete="email"
                    maxLength={320}
                    invalide={erreurs.email !== undefined}
                    value={saisie.email}
                    onChange={(e) => {
                      modifier('email', e.target.value)
                    }}
                  />
                )}
              </FormField>

              <FormField label="Téléphone" aide="Facultatif." erreur={erreurs.telephone}>
                {(champ) => (
                  <Input
                    {...champ}
                    type="tel"
                    autoComplete="tel"
                    maxLength={40}
                    value={saisie.telephone}
                    onChange={(e) => {
                      modifier('telephone', e.target.value)
                    }}
                  />
                )}
              </FormField>
            </div>

            <FormField
              label="Votre message"
              requis
              erreur={erreurs.message}
              aide="Nombre de places, formules vendues, échéance : plus c’est précis, plus la réponse l’est."
            >
              {(champ) => (
                <Textarea
                  {...champ}
                  rows={6}
                  maxLength={4000}
                  invalide={erreurs.message !== undefined}
                  value={saisie.message}
                  onChange={(e) => {
                    modifier('message', e.target.value)
                  }}
                />
              )}
            </FormField>

            {/* ★ Le champ piège. Sorti du flux par positionnement — jamais
                `display:none`, que certains robots savent ignorer —, retiré de
                l’ordre de tabulation et de l’arbre d’accessibilité : il
                n’existe ni pour l’œil, ni pour le clavier, ni pour un lecteur
                d’écran. Son étiquette reste réelle pour le robot qui lit le
                balisage : c’est l’appât. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -left-[9999px] h-0 w-0 overflow-hidden"
            >
              <label htmlFor="contact-site-web">Site web (ne pas remplir)</label>
              <input
                id="contact-site-web"
                name="site-web"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={piege}
                onChange={(e) => {
                  setPiege(e.target.value)
                }}
              />
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <Button type="submit" taille="lg" disabled={envoi} chargement={envoi}>
                {envoi ? 'Envoi en cours…' : 'Envoyer ma demande'}
              </Button>
              <p className="text-sm text-slate-500">
                Votre message part chez l’éditeur, ILYGO Sàrl. Rien n’est transmis à un tiers.
              </p>
            </div>
          </form>
        ) : (
          <div className="rounded-carte border border-rideau-300 bg-rideau-50 p-6 sm:p-7">
            <p
              ref={confirmation}
              tabIndex={-1}
              role="status"
              className="text-base font-semibold text-slate-900 focus:outline-2 focus:outline-offset-4 focus:outline-rideau-700"
            >
              {succes}
            </p>
            <p className="mt-2 text-[0.9375rem] text-slate-600">
              Nous répondons depuis {EMAIL_ILYGO}&nbsp;: si notre réponse tarde, regardez dans vos
              indésirables.
            </p>
            <Button
              type="button"
              variante="secondaire"
              className="mt-5"
              onClick={() => {
                ouvertureMs.current = performance.now()
                setSucces(null)
              }}
            >
              Écrire un autre message
            </Button>
          </div>
        )}
      </div>
    </Section>,
  )
}
